import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {Entity} from "../../../../entity/entity";
import {GuruEntity} from "../../../../model/guru-entity";
import {ModelService} from "../../../../services/model-service";
import {SpecificationService} from "../../../../services/specification-service";
import {Router} from "@angular/router";
import {StringUtils} from "../../../../utils/string-utils";
import {firstValueFrom} from "rxjs";
import {Specification} from "../../../../entity/specification";
import {SpecificationCharacteristic} from "../../../../entity/specification-characteristic";
import {SelectionUtils} from "../../../../configurator/selection-utils";
import {FormsModule} from "@angular/forms";

import {MatButton} from "@angular/material/button";

@Component({
    selector: 'app-clm-riser-project-defaults',
    imports: [
    FormsModule,
    MatButton
],
    templateUrl: './clm-riser-project-defaults.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './clm-riser-project-defaults.component.css'
})
export class ClmRiserProjectDefaultsComponent implements OnInit {
  private readonly modelService = inject(ModelService);
  private readonly specificationService = inject(SpecificationService);
  private readonly router = inject(Router);


  readonly entity = input.required<Entity>();
  readonly errorEvent = output<any>();
  private engineKey!: string;
  private invalidEntities!: string[];
  readonly isChanged = signal<boolean>(false);
  readonly isBusy = signal<boolean>(undefined!);
  topInsulationCutback!: GuruEntity;
  bottomInsulationCutback!: GuruEntity;
  riserEndTreatment!: GuruEntity;
  riserCopperType!: GuruEntity;
  supplyInsulation!: GuruEntity;
  returnInsulation!: GuruEntity;
  drainCopperType!: GuruEntity;
  drainInsulationThickness!: GuruEntity;

  ngOnInit(): void {
    if (StringUtils.isBlank(this.entity()?.id?.entityId)) {
      this.backToHome();
    }
    this.isBusy.set(true);
    this.initializeData().then(() => this.isBusy.set(false), (error: any) => this.handleError(error));
  }

  private async initializeData(): Promise<void> {
    this.engineKey = await firstValueFrom<string>(this.modelService.getEngineKey('CLMProjectDefaults'));
    const specifications: Specification[] = await firstValueFrom<Specification[]>(this.specificationService.getSpecifications(this.entity().id));
    if ((specifications?.length || 0) > 0) {
      const filtered: Specification[] = specifications.filter((spec: Specification) => spec.specificationType === 'PRO_USC');
      if (filtered.length === 1) {
        await firstValueFrom<string>(this.modelService.loadConfiguration(this.engineKey, filtered[0].specificationCharacteristics));
      }
    }
    await this.loadUserSelections();
  }

  private async loadUserSelections(): Promise<void> {
    const userSelections: GuruEntity[] = await firstValueFrom<GuruEntity[]>(this.modelService.getUserSelections(this.engineKey));
    this.topInsulationCutback = SelectionUtils.getEntity(userSelections, 'USInsulationCutbackTop');
    this.bottomInsulationCutback = SelectionUtils.getEntity(userSelections, 'USInsulationCutbackBottom');
    this.riserEndTreatment = SelectionUtils.getEntity(userSelections, 'USEndTreatment');
    this.riserCopperType = SelectionUtils.getEntity(userSelections, 'USSRRiserCopperType');
    this.supplyInsulation = SelectionUtils.getEntity(userSelections, 'USSupplyRiserInsulationThickness');
    this.returnInsulation = SelectionUtils.getEntity(userSelections, 'USReturnRiserInsulationThickness');
    this.drainCopperType = SelectionUtils.getEntity(userSelections, 'USDRRiserCopperType');
    this.drainInsulationThickness = SelectionUtils.getEntity(userSelections, 'USDRInsulationThickness');
  }

  private backToHome(): void {
    this.router.navigate(['/', 'main']).then(() => this.isBusy.set(false));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorEvent.emit(undefined);
    this.invalidEntities = null!;
  }

  private handleError(error: any) {
    this.isBusy.set(false);
    this.errorEvent.emit(error);
  }

  validateSelections(entity: GuruEntity, newValue: string): void {
    this.resetStatus();
    this.updateSelectedValue(entity, newValue).then(() => {
      this.isBusy.set(false);
      this.isChanged.set(true);
    }, (error: any) => this.handleError(error));
  }

  private async updateSelectedValue(entity: GuruEntity, newValue: string): Promise<void> {
    await firstValueFrom<string>(this.modelService.setValue(entity.Name, this.engineKey, newValue));
    await firstValueFrom<boolean>(this.modelService.isComplete(this.engineKey));
    await firstValueFrom<boolean>(this.modelService.isValid(this.engineKey));
    this.invalidEntities = await firstValueFrom<string[]>(this.modelService.getInvalidEntities(this.engineKey));
  }

  canSave(): boolean {
    return !this.isBusy();
  }

  saveChanges(): void {
    if (this.canSave()) {
      this.resetStatus();
      this.updateProjectDefaults().then(() => {
        this.isBusy.set(false);
        this.isChanged.set(false);
      }, (error: any) => this.handleError(error));
    }
  }

  private async updateProjectDefaults(): Promise<void> {
    await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierSKU'));
    const characteristics: SpecificationCharacteristic[] = await firstValueFrom<SpecificationCharacteristic[]>(this.modelService.getCharacteristics(this.engineKey));
    if ((characteristics?.length || 0) > 0) {
      await firstValueFrom<string>(this.specificationService.saveProjectDefaults(this.entity().id, characteristics));
    }
  }

  isInvalid(entity: GuruEntity): boolean {
    return (this.invalidEntities?.length || 0) > 0 && this.invalidEntities.indexOf(entity.Name) >= 0;
  }

}
